import { useEffect, useRef, useState } from 'react';
import { Crosshair, MapPin } from 'lucide-react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { api } from '../../api/client';
import type { TypeAvis } from '../../types';
import { useRole } from '../../context/RoleContext';
import { useFenetreModale } from '../demande/useFenetreModale';
import { cible, nommer, type ElementDesigne } from './pointeur';

// « Votre avis » (DECISIONS §32) : une fenêtre par-dessus la page. Elle sait déjà où l'on est (page + lien), « Montrer l'endroit sur la page »
// désigne un bouton, une carte ou un champ précis, et tout ce qui part avec le message est écrit dans la fenêtre.

const CLE_BROUILLON = 'avis_brouillon';
const TYPES: { id: TypeAvis; libelle: string }[] = [
  { id: 'probleme', libelle: 'Quelque chose ne marche pas' },
  { id: 'idee', libelle: "Une idée d'amélioration" },
  { id: 'comprends', libelle: 'Je ne comprends pas' },
  { id: 'autre', libelle: 'Autre' },
];

function lireBrouillon(): { message: string; type: TypeAvis | null } {
  try {
    const b = JSON.parse(sessionStorage.getItem(CLE_BROUILLON) ?? 'null');
    if (b && typeof b.message === 'string') return { message: b.message, type: b.type ?? null };
  } catch { /* stockage indisponible : pas de brouillon */ }
  return { message: '', type: null };
}
function ecrireBrouillon(message: string, type: TypeAvis | null) {
  try {
    if (message.trim() || type) sessionStorage.setItem(CLE_BROUILLON, JSON.stringify({ message, type }));
    else sessionStorage.removeItem(CLE_BROUILLON);
  } catch { /* idem */ }
}

type Mode = 'saisie' | 'pointage' | 'merci';

export function FenetreAvis({ lieu, onFermer }: { lieu: string; onFermer: () => void }) {
  const brouillon0 = useRef(lireBrouillon());
  const [message, setMessage] = useState(brouillon0.current.message);
  const [type, setType] = useState<TypeAvis | null>(brouillon0.current.type);
  const [element, setElement] = useState<ElementDesigne | null>(null);
  const [suivi, setSuivi] = useState(true);
  const [mode, setMode] = useState<Mode>('saisie');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [brouillonRepris] = useState(() => !!brouillon0.current.message.trim());
  // La page et son état au moment de l'ouverture : c'est ce que la gestion rouvrira.
  const lien = useRef(window.location.hash || '#tableau');
  // Le bouton « Votre avis » du menu : le focus y revient à la fermeture (même après un détour par le pointeur).
  const origine = useRef(document.activeElement as HTMLElement | null);

  const fermer = (garder: boolean) => {
    if (!garder) ecrireBrouillon('', null);
    onFermer();
    const o = origine.current;
    setTimeout(() => { if (o?.isConnected) o.focus(); }, 0);
  };

  useEffect(() => { if (mode === 'saisie') ecrireBrouillon(message, type); }, [message, type, mode]);

  if (mode === 'pointage') {
    return <Pointage onChoisi={(el) => { if (el) setElement(el); setMode('saisie'); }} />;
  }

  const envoyer = async () => {
    if (!message.trim()) { setErreur("Écrivez au moins une phrase : c'est ce que nous lirons."); return; }
    setEnvoi(true);
    setErreur(null);
    try {
      await api.envoyerAvis({
        message, type, page: lieu, lien: lien.current, element,
        navigateur: navigator.userAgent.slice(0, 300), fenetre: `${window.innerWidth}×${window.innerHeight}`, suivi,
      });
      ecrireBrouillon('', null);
      setMode('merci');
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "L'envoi a échoué. Votre message est conservé : réessayez.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Saisie
      mode={mode} lieu={lieu} lien={lien.current} message={message} type={type} element={element} suivi={suivi} erreur={erreur} envoi={envoi}
      brouillonRepris={brouillonRepris}
      onMessage={(m) => { setMessage(m); if (m.trim()) setErreur(null); }}
      onType={(t) => setType((c) => (c === t ? null : t))}
      onSuivi={setSuivi} onPointer={() => setMode('pointage')} onRetirerElement={() => setElement(null)}
      onFermer={() => fermer(true)} onAnnuler={() => fermer(false)} onEnvoyer={envoyer}
    />
  );
}

interface SaisieProps {
  mode: Mode; lieu: string; lien: string; message: string; type: TypeAvis | null; element: ElementDesigne | null; suivi: boolean;
  erreur: string | null; envoi: boolean; brouillonRepris: boolean;
  onMessage: (m: string) => void; onType: (t: TypeAvis) => void; onSuivi: (v: boolean) => void;
  onPointer: () => void; onRetirerElement: () => void; onFermer: () => void; onAnnuler: () => void; onEnvoyer: () => void;
}

function Saisie(p: SaisieProps) {
  const { user } = useAuth();
  const { roles } = useRole();
  const zone = useRef<HTMLTextAreaElement>(null);
  const retirer = useRef<HTMLButtonElement>(null);
  const fermerBtn = useRef<HTMLButtonElement>(null);
  const { ref } = useFenetreModale(p.onFermer, {
    // Au retour du pointeur, le focus va sur l'élément désigné ; sinon dans le message. Le focus est rendu par FenetreAvis.
    focusInitial: () => retirer.current ?? zone.current ?? fermerBtn.current,
    sansRetourFocus: () => true,
    auDessus: () => p.envoi,
  });
  const qui = `${user?.name ?? ''}${roles.length ? ` · ${roles.join(', ')}` : ''}`;

  if (p.mode === 'merci') {
    return (
      <div className="modal-overlay" onClick={p.onFermer}>
        <div className="modal" ref={ref} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="avis-titre">
          <div className="modal-header">
            <h3 className="modal-title" id="avis-titre">Merci, c'est transmis</h3>
            <button type="button" className="close-btn" ref={fermerBtn} onClick={p.onFermer} aria-label="Fermer">✕</button>
          </div>
          <div className="modal-body">
            <p className="text-sm text-ink-2">
              L'équipe gestion lira votre message.{p.suivi ? ' Vous serez prévenu·e dans la cloche quand il sera traité.' : ''}
            </p>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-accent" onClick={p.onFermer}>Fermer</button></div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={p.onFermer}>
      <div className="modal avis-modal" ref={ref} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="avis-titre">
        <div className="modal-header">
          <h3 className="modal-title" id="avis-titre">Votre avis sur l'application</h3>
          <button type="button" className="close-btn" ref={fermerBtn} onClick={p.onFermer} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">
          <p className="text-sm text-ink-2 avis-intro">
            Une idée, un blocage, un mot qui ne se comprend pas : dites-le ici, depuis n'importe quelle page. L'équipe gestion lit chaque message.
          </p>
          {p.brouillonRepris && <p className="avis-repris">Votre brouillon a été repris.</p>}

          <div className="form-group">
            <span className="form-label">Où ?</span>
            <div className="avis-chip"><MapPin className="icon-sm" aria-hidden="true" /><span>{p.lieu}</span></div>
            {p.element && (
              <div className="avis-chip avis-chip-el">
                <Crosshair className="icon-sm" aria-hidden="true" />
                <span>{p.element.libelle}</span>
                <button type="button" className="avis-chip-x" ref={retirer} onClick={p.onRetirerElement} aria-label="Retirer l'élément désigné">✕</button>
              </div>
            )}
            <div className="avis-actions">
              <button type="button" className="btn btn-sm" onClick={p.onPointer}>
                {p.element ? 'Montrer un autre endroit' : "Montrer l'endroit sur la page"}
              </button>
            </div>
            <p className="avis-aide">La fenêtre sait déjà sur quelle page vous êtes. « Montrer l'endroit » précise le bouton, la carte ou le champ dont vous parlez.</p>
          </div>

          <div className="form-group">
            <span className="form-label" id="avis-type-l">De quoi s'agit-il ? <span className="avis-opt">facultatif</span></span>
            <div className="selectable-group" role="group" aria-labelledby="avis-type-l">
              {TYPES.map((t) => (
                <button key={t.id} type="button" className={`selectable ${p.type === t.id ? 'active' : ''}`} aria-pressed={p.type === t.id} onClick={() => p.onType(t.id)}>
                  {t.libelle}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="avis-message">Votre message</label>
            <textarea id="avis-message" ref={zone} className="form-textarea" rows={5} value={p.message} onChange={(e) => p.onMessage(e.target.value)}
              maxLength={4000} aria-invalid={!!p.erreur} aria-describedby={p.erreur ? 'avis-erreur' : undefined}
              placeholder="Ce que vous vouliez faire, ce qui s'est passé, ce que vous attendiez." />
            {p.erreur && <p className="avis-erreur" id="avis-erreur" role="alert">{p.erreur}</p>}
          </div>

          <label className="avis-check"><input type="checkbox" checked={p.suivi} onChange={(e) => p.onSuivi(e.target.checked)} /> Me prévenir dans la cloche quand c'est traité</label>

          <div className="avis-contexte">
            <strong>Envoyé avec votre message</strong>
            <ul>
              <li>Vous : {qui}</li>
              <li>La page et son état exact, sous forme de lien : <code>{p.lien}</code></li>
              <li>Navigateur et taille de la fenêtre, date et heure</li>
            </ul>
            <p>Pas d'information de santé ici, ni sur vous ni sur une autre personne.</p>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" onClick={p.onAnnuler} disabled={p.envoi}>Annuler</button>
          <button type="button" className="btn btn-accent" onClick={p.onEnvoyer} disabled={p.envoi}>
            {p.envoi ? <span className="spinner spinner-sm" aria-label="Envoi en cours" /> : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** La fenêtre s'efface : un clic (ou Tab puis Entrée) désigne l'élément ; Échap annule. */
function Pointage({ onChoisi }: { onChoisi: (el: ElementDesigne | null) => void }) {
  const [survol, setSurvol] = useState<{ x: number; y: number; w: number; h: number; libelle: string } | null>(null);
  const barre = useRef<HTMLDivElement>(null);
  const annuler = useRef<HTMLButtonElement>(null);
  const choisi = useRef(onChoisi);
  choisi.current = onChoisi;

  useEffect(() => {
    const ignorer = (e: Element) => !!e.closest('[data-avis]');
    const montrer = (el: Element | null) => {
      if (!el) { setSurvol(null); return; }
      const r = el.getBoundingClientRect();
      setSurvol({ x: r.left, y: r.top, w: r.width, h: r.height, libelle: nommer(el).libelle });
    };
    const surSouris = (e: MouseEvent) => montrer(cible(document.elementFromPoint(e.clientX, e.clientY), ignorer));
    const surFocus = (e: FocusEvent) => { const t = e.target as Element; if (!ignorer(t)) montrer(cible(t, ignorer)); };
    const surClic = (e: MouseEvent) => {
      const t = e.target as Element;
      if (ignorer(t)) return;
      e.preventDefault(); e.stopPropagation();
      const el = cible(t, ignorer);
      if (el) choisi.current(nommer(el));
    };
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); choisi.current(null); return; }
      const actif = document.activeElement;
      if (e.key === 'Enter' && actif && !ignorer(actif)) {
        const el = cible(actif, ignorer);
        if (el) { e.preventDefault(); e.stopPropagation(); choisi.current(nommer(el)); }
      }
    };
    document.addEventListener('mousemove', surSouris);
    document.addEventListener('focusin', surFocus);
    document.addEventListener('click', surClic, true);
    document.addEventListener('keydown', surTouche, true);
    document.body.classList.add('avis-pointage');
    annuler.current?.focus();
    return () => {
      document.removeEventListener('mousemove', surSouris);
      document.removeEventListener('focusin', surFocus);
      document.removeEventListener('click', surClic, true);
      document.removeEventListener('keydown', surTouche, true);
      document.body.classList.remove('avis-pointage');
    };
  }, []);

  return (
    <>
      {survol && (
        <div className="avis-surligne" data-avis style={{ left: survol.x - 2, top: survol.y - 2, width: survol.w + 4, height: survol.h + 4 }}>
          <span className="avis-surligne-lbl">{survol.libelle}</span>
        </div>
      )}
      <div className="avis-barre" data-avis ref={barre} role="status">
        <span>Cliquez sur l'élément dont vous voulez parler. Au clavier : Tab jusqu'à l'élément, puis Entrée.</span>
        <button type="button" className="btn btn-sm" ref={annuler} onClick={() => onChoisi(null)}>Annuler (Échap)</button>
      </div>
    </>
  );
}
